import React, { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Header from './Header';

export interface AritoModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  isFullScreen?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullWidth?: boolean;
  title?: string;
  titleIcon?: ReactNode;
  showHeader?: boolean;
  isSize?: boolean;
}

export const AritoModal = ({
  open,
  onClose,
  children,
  isFullScreen: initialFullScreen = false,
  maxWidth = 'md',
  title = '',
  titleIcon,
  showHeader = true,
  isSize = true
}: AritoModalProps) => {
  const [isFullScreen, setIsFullScreen] = useState(initialFullScreen);
  const [mounted, setMounted] = useState(false);

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  const getWidth = () => {
    if (isFullScreen) return '100%';

    switch (maxWidth) {
      case 'xs':
        return '400px';
      case 'sm':
        return '600px';
      case 'md':
        return '800px';
      case 'lg':
        return '1000px';
      case 'xl':
        return '1200px';
      default:
        return '90%';
    }
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, open]);

  if (!mounted || !open) return null;

  return createPortal(
    <div aria-modal='true' className='fixed inset-0 z-[1300] bg-black/50' role='dialog'>
      <div
        className={`flex flex-col overflow-hidden bg-white shadow-lg ${isFullScreen ? 'rounded-none' : 'rounded'}`}
        style={{
          position: 'absolute',
          top: isFullScreen ? 0 : '50%',
          left: isFullScreen ? 0 : '50%',
          transform: isFullScreen ? 'none' : 'translate(-50%, -50%)',
          width: getWidth(),
          height: isFullScreen ? '100%' : 'auto',
          maxHeight: isFullScreen ? '100%' : '90vh',
          minHeight: isFullScreen ? '100%' : '200px'
        }}
      >
        {showHeader && (
          <Header
            title={title}
            titleIcon={titleIcon}
            isFullScreen={isFullScreen}
            toggleFullScreen={toggleFullScreen}
            onClose={onClose}
            isSize={isSize}
          />
        )}
        {children}
      </div>
    </div>,
    document.body
  );
};

export default AritoModal;
